import { CardElement } from "@/types/battle"
import { DominantLeague } from "@/types/faction"
import { WorldMapPointKind } from "@/types/location"
import {
  MAP_BOSS_MARKERS,
  MAP_SCAN_LOCATION_BY_LEAGUE,
  MAP_BANK_MARKER,
  MAP_CITY_HALL_MARKER,
  MAP_HOTEL_MARKER,
  MAP_WISE_TREE_MARKER,
  MAP_HANGAR_MARKER,
  MAP_AIRSHIP_MARKER,
  MAP_BASE_TITAN_MARKER,
  MAP_BASE_ATLANT_MARKER,
  MAP_MAIL_MARKER,
  MAP_SHOP_MARKER,
  MAP_TREASURE_MARKER,
  MAP_STARFALL_MARKER,
  MAP_WORLD_BOSS_MARKER,
  MAP_PLAYER_MARKERS_BY_ELEMENT,
} from "@/constants/mapMarkerAssets"
import {
  AFFILIATION_ELEMENTS,
} from "@/constants/affiliationInfo"
import {
  normalizeAvatarVariant,
  resolvePlayerAvatarModule,
} from "@/constants/playerAvatars"

const MAP_TRENT_MARKER = MAP_WISE_TREE_MARKER

export type MapScanLeagueKey = DominantLeague
export type TreasureMapMode = "treasure" | "starfall"

export type MapMarkerIconSet = {
  scanByLeague: Record<MapScanLeagueKey, string>
  boss: string[]
  worldboss: string
  shop: string
  mail: string
  treasure: string
  starfall: string
  bank: string
  cityhall: string
  hotel: string
  hangar: string
  airship: string
  baseTitan: string
  baseAtlant: string
  wisetree: string
  trent: string
  playerByElement: Partial<Record<CardElement, string>>
  playerByElementVariant: Partial<Record<string, string>>
}

export type MapMarkerIconRef =
  | { kind: "shop"; index: number }
  | { kind: "mail"; index: number }
  | { kind: "treasure"; index: number }
  | { kind: "starfall"; index: number }
  | { kind: "bank"; index: number }
  | { kind: "cityhall"; index: number }
  | { kind: "hotel"; index: number }
  | { kind: "hangar"; index: number }
  | { kind: "worldboss"; index: number }
  | { kind: "wisetree"; index: number }
  | { kind: "trent"; index: number }
  | { kind: "boss"; index: number }
  | { kind: "scan"; league: MapScanLeagueKey }

function hashSeed(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(h)
}

function pickVariantIndex(seed: string, count: number): number {
  if (count <= 1) return 0
  return hashSeed(seed) % count
}

export function normalizeScanLeague(
  league?: DominantLeague | null
): MapScanLeagueKey {
  if (league === "TITAN" || league === "ATLANT") return league
  return "NEUTRAL"
}

export function resolvePlayerMapIconUri(
  icons: MapMarkerIconSet,
  element?: CardElement | null,
  avatarVariant?: number | null
): string | undefined {
  const key =
    element && icons.playerByElement[element] ? element : ("FIRE" as CardElement)
  const variant = normalizeAvatarVariant(avatarVariant)
  const variantKey = `${key}-${variant}`
  return (
    icons.playerByElementVariant[variantKey] ??
    icons.playerByElement[key] ??
    icons.playerByElement.FIRE ??
    Object.values(icons.playerByElement).find(Boolean)
  )
}

export function resolveMapMarkerIconRef(
  kind: WorldMapPointKind,
  pointId: string,
  dominantLeague?: DominantLeague | null,
  treasureMode?: TreasureMapMode | null
): MapMarkerIconRef {
  if (kind === "cardshop" || kind === "boostershop") return { kind: "shop", index: 0 }
  if (kind === "postoffice") return { kind: "mail", index: 0 }
  if (kind === "treasure") {
    return treasureMode === "starfall"
      ? { kind: "starfall", index: 0 }
      : { kind: "treasure", index: 0 }
  }
  if (kind === "bank") return { kind: "bank", index: 0 }
  if (kind === "cityhall") return { kind: "cityhall", index: 0 }
  if (kind === "hotel") return { kind: "hotel", index: 0 }
  if (kind === "wisetree") return { kind: "wisetree", index: 0 }
  if (kind === "hangar") return { kind: "hangar", index: 0 }
  if (kind === "worldboss") return { kind: "worldboss", index: 0 }
  if (kind === "boss") {
    return { kind: "boss", index: pickVariantIndex(pointId, MAP_BOSS_MARKERS.length) }
  }
  return { kind: "scan", league: normalizeScanLeague(dominantLeague) }
}

export function loadMapMarkerIcons(): Promise<MapMarkerIconSet> {
  const playerByElement: Partial<Record<CardElement, string>> = {}
  const playerByElementVariant: Partial<Record<string, string>> = {}
  for (const el of AFFILIATION_ELEMENTS) {
    playerByElement[el] = MAP_PLAYER_MARKERS_BY_ELEMENT[el]
    for (let v = 1; v <= 3; v++) {
      playerByElementVariant[`${el}-${v}`] = resolvePlayerAvatarModule(el, v)
    }
  }
  return Promise.resolve({
    scanByLeague: { ...MAP_SCAN_LOCATION_BY_LEAGUE },
    boss: [...MAP_BOSS_MARKERS],
    worldboss: MAP_WORLD_BOSS_MARKER,
    shop: MAP_SHOP_MARKER,
    mail: MAP_MAIL_MARKER,
    treasure: MAP_TREASURE_MARKER,
    starfall: MAP_STARFALL_MARKER,
    bank: MAP_BANK_MARKER,
    cityhall: MAP_CITY_HALL_MARKER,
    hotel: MAP_HOTEL_MARKER,
    hangar: MAP_HANGAR_MARKER,
    airship: MAP_AIRSHIP_MARKER,
    baseTitan: MAP_BASE_TITAN_MARKER,
    baseAtlant: MAP_BASE_ATLANT_MARKER,
    wisetree: MAP_WISE_TREE_MARKER,
    trent: MAP_TRENT_MARKER,
    playerByElement,
    playerByElementVariant,
  })
}

export function resolveMapMarkerIconUri(
  icons: MapMarkerIconSet,
  kind: WorldMapPointKind,
  pointId: string,
  dominantLeague?: DominantLeague | null,
  treasureMode?: TreasureMapMode | null
): string {
  const ref = resolveMapMarkerIconRef(kind, pointId, dominantLeague, treasureMode)
  if (ref.kind === "shop") return icons.shop
  if (ref.kind === "mail") return icons.mail
  if (ref.kind === "treasure") return icons.treasure
  if (ref.kind === "starfall") return icons.starfall
  if (ref.kind === "bank") return icons.bank
  if (ref.kind === "cityhall") return icons.cityhall
  if (ref.kind === "hotel") return icons.hotel
  if (ref.kind === "hangar") return icons.hangar
  if (ref.kind === "worldboss") return icons.worldboss
  if (ref.kind === "wisetree") return icons.wisetree
  if (ref.kind === "trent") return icons.trent
  if (ref.kind === "scan") {
    return icons.scanByLeague[ref.league] ?? icons.scanByLeague.NEUTRAL ?? icons.shop
  }
  return icons.boss[ref.index] ?? icons.boss[0] ?? icons.shop
}

export function buildMapIconsPayload(
  icons: MapMarkerIconSet,
  playerElement?: CardElement | null,
  playerAvatarVariant?: number | null
) {
  return {
    scanByLeague: icons.scanByLeague,
    boss: icons.boss,
    worldboss: icons.worldboss,
    shop: icons.shop,
    mail: icons.mail,
    treasure: icons.treasure,
    starfall: icons.starfall,
    bank: icons.bank,
    cityhall: icons.cityhall,
    hotel: icons.hotel,
    hangar: icons.hangar,
    airship: icons.airship,
    baseTitan: icons.baseTitan,
    baseAtlant: icons.baseAtlant,
    wisetree: icons.wisetree,
    trent: icons.trent,
    player: resolvePlayerMapIconUri(icons, playerElement, playerAvatarVariant),
    playerByElement: icons.playerByElement,
    playerByElementVariant: icons.playerByElementVariant,
  }
}
